import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import GeneralApiService from '../axios/Axios';
import { toast } from 'react-toastify';
import { Box, Button, Chip, Modal, ModalClose, ModalDialog, Table, Typography } from '@mui/joy';

interface RecentCallLog {
  id: number;
  timestamp: string;
  duration: number;
  call_type: 'inbound' | 'outbound' | string;
  answer_status: string;
  sales_user?: string | null;
  sales_username?: string | null;
}

interface PendingCallConfirmation {
  phoneNumber: string;
  displayName?: string;
  recentLogs: RecentCallLog[];
}

interface CallContextType {
  device: Device | null;
  call: Call | null;
  connectionStatus: string;
  isReady: boolean;
  makeCall: (phoneNumber: string, displayName?: string) => Promise<void>;
  requestMicrophonePermission: () => Promise<boolean>;
  hangupCall: () => void;
  acceptCall: () => void;
  rejectCall: () => void;
  incomingCall: Call | null;
  activeCallLabel: string | null;
  incomingCallLabel: string | null;
  hasMicrophone: boolean;
  microphonePermission: "unknown" | "granted" | "denied";
  muted: boolean;
  toggleMute: () => void;
}

const CallContext = createContext<CallContextType | undefined>(undefined);

const TWILIO_EDGE = process.env.REACT_APP_TWILIO_EDGE || "dublin";
const TOKEN_REFRESH_WARNING_MS = 5 * 60 * 1000;
const TOKEN_REFRESH_INTERVAL_MS = 30 * 60 * 1000;
const DEVICE_RETRY_MS = 10 * 1000;

const tokenErrorCodes = new Set([20101, 20104, 31204, 31205]);
const transportErrorCodes = new Set([31003, 31005, 31009]);

export function CallProvider({ children }: { children: React.ReactNode }) {
  const [device, setDevice] = useState<Device | null>(null);
  const [call, setCall] = useState<Call | null>(null);
  const [incomingCall, setIncomingCall] = useState<Call | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<string>('offline');
  const [isReady, setIsReady] = useState(false);
  const [activeCallLabel, setActiveCallLabel] = useState<string | null>(null);
  const [incomingCallLabel, setIncomingCallLabel] = useState<string | null>(null);
  const [hasMicrophone, setHasMicrophone] = useState(true);
  const [microphonePermission, setMicrophonePermission] = useState<"unknown" | "granted" | "denied">("unknown");
  const [muted, setMuted] = useState(false);
  const [serverCallStatus, setServerCallStatus] = useState<string | null>(null);
  const [pendingCallConfirmation, setPendingCallConfirmation] = useState<PendingCallConfirmation | null>(null);

  // Refs to avoid stale closure issues inside event handlers
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const incomingRef = useRef<Call | null>(null);
  const connectionStatusRef = useRef<string>('offline');
  const microphonePermissionRef = useRef<"unknown" | "granted" | "denied">("unknown");

  // Simple sound engine (no binary assets): generate tones via WebAudio
  const audioCtxRef = useRef<AudioContext | null>(null);
  const ringIntervalRef = useRef<number | null>(null);
  const ringTimeoutsRef = useRef<number[]>([]);

  useEffect(() => {
    deviceRef.current = device;
  }, [device]);
  useEffect(() => {
    callRef.current = call;
  }, [call]);
  useEffect(() => {
    incomingRef.current = incomingCall;
  }, [incomingCall]);
  useEffect(() => {
    connectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);
  useEffect(() => {
    microphonePermissionRef.current = microphonePermission;
  }, [microphonePermission]);

  const ensureAudioContext = async () => {
    if (typeof window === "undefined") return;
    const Ctx = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext | undefined;
    if (!Ctx) return;
    if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
    if (audioCtxRef.current.state === "suspended") {
      try {
        await audioCtxRef.current.resume();
      } catch {
        // ignore
      }
    }
  };

  const playTone = async (freq: number, durationMs = 140, volume = 0.06) => {
    await ensureAudioContext();
    const ctx = audioCtxRef.current;
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.value = freq;

    gain.gain.value = 0;
    osc.connect(gain);
    gain.connect(ctx.destination);

    const now = ctx.currentTime;
    // soft attack/release to avoid clicks
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(volume, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000);

    osc.start(now);
    osc.stop(now + durationMs / 1000 + 0.02);
  };

  const stopRingtone = () => {
    if (ringIntervalRef.current) {
      window.clearInterval(ringIntervalRef.current);
      ringIntervalRef.current = null;
    }
    ringTimeoutsRef.current.forEach((t) => window.clearTimeout(t));
    ringTimeoutsRef.current = [];
  };

  const startRingtone = async (kind: "incoming" | "outgoing") => {
    stopRingtone();
    // If we can't play audio yet (no user gesture), playTone() will fail silently in some browsers.
    // That's okay; UI will still show the incoming call.
    const baseFreq = kind === "incoming" ? 880 : 660;

    const tick = () => {
      // two short beeps per cycle
      ringTimeoutsRef.current.push(window.setTimeout(() => void playTone(baseFreq, 120), 0));
      ringTimeoutsRef.current.push(window.setTimeout(() => void playTone(baseFreq, 120), 220));
    };

    tick();
    ringIntervalRef.current = window.setInterval(tick, 2600);
  };

  // Best-effort: read existing browser permission state (won't prompt).
  useEffect(() => {
    let cancelled = false;

    const checkPermissionState = async () => {
      try {
        // Permissions API is not available everywhere.
        const perms = (navigator as any)?.permissions;
        if (!perms?.query) return;
        const status = await perms.query({ name: "microphone" });
        if (cancelled) return;
        if (status?.state === "granted") setMicrophonePermission("granted");
        else if (status?.state === "denied") setMicrophonePermission("denied");
        else setMicrophonePermission("unknown");

        status?.addEventListener?.("change", () => {
          if (cancelled) return;
          if (status.state === "granted") setMicrophonePermission("granted");
          else if (status.state === "denied") setMicrophonePermission("denied");
          else setMicrophonePermission("unknown");
        });
      } catch {
        // Ignore; we'll resolve on user gesture.
      }
    };

    checkPermissionState();
    return () => {
      cancelled = true;
    };
  }, []);

  const requestMicrophonePermission = async (): Promise<boolean> => {
    try {
      // If not a secure context, browsers will refuse mic access without prompting.
      if (typeof window !== "undefined" && !window.isSecureContext) {
        toast.error("Microphone access requires HTTPS (or localhost).");
        setMicrophonePermission("denied");
        return false;
      }

      if (!navigator?.mediaDevices?.getUserMedia) return true;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setMicrophonePermission("granted");

      // user gesture happened; unlock audio so ringtone can play
      await ensureAudioContext();

      // Refresh device list right after granting permission so hasMicrophone updates without reload.
      try {
        const devices = await navigator.mediaDevices.enumerateDevices?.();
        if (devices) {
          setHasMicrophone(devices.some((d: any) => d.kind === "audioinput"));
        }
      } catch {}

      return true;
    } catch (e: any) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setMicrophonePermission("denied");
        // If the user previously blocked, Chrome won't re-prompt; they must change site settings.
        toast.error("Microphone blocked in browser settings. Allow it for this site and refresh.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        // This is a hardware/device issue, not a permission issue.
        // Keep permission as unknown so UI can show "plug in mic" or "enable mic" appropriately.
        setMicrophonePermission("unknown");
        setHasMicrophone(false);
        toast.error("No microphone device found. Plug in a headset/mic.");
      } else {
        setMicrophonePermission("unknown");
        toast.error(`Microphone error: ${e?.message || String(e)}`);
      }
      return false;
    }
  };

  // Track whether there is an audio input device connected (headset/mic).
  // This does NOT prompt for permissions; it simply enumerates devices.
  useEffect(() => {
    let cancelled = false;

    const checkMicrophone = async () => {
      try {
        if (!navigator?.mediaDevices?.enumerateDevices) return;
        const devices = await navigator.mediaDevices.enumerateDevices();
        // Check for audioinput explicitly.
        // NOTE: On some browsers/OSs, if no mic is plugged in, devices list might be empty 
        // OR contain only default output.
        // If permission is GRANTED, we trust enumerateDevices() completely.
        // If permission is NOT granted, enumerateDevices() might hide labels/ids but should show type.
        
        const hasAudioInput = devices.some((d) => d.kind === "audioinput");
        const perm = microphonePermissionRef.current;
        
        if (!cancelled) {
          if (perm === "granted") {
            // If granted, trust the device list.
            setHasMicrophone(hasAudioInput);
          } else {
             // If NOT granted, sometimes browsers hide devices or return empty lists.
             // We can't be sure "no audioinput" means "no hardware" vs "blocked enumeration".
             // However, to fix "Ready" showing when no mic exists, we must eventually trust false.
             // Strategy: If we detect 0 audio inputs, we say no mic.
             // But if we detect 0 devices total (privacy masking), we might assume optimistic true.
             if (devices.length > 0 && !hasAudioInput) {
                 setHasMicrophone(false);
             } else {
                 // Fallback optimistic (or correct if devices found)
                 setHasMicrophone(hasAudioInput || devices.length === 0);
             }
          }
        }
      } catch {
        // If enumeration fails for any reason, don't block answering calls.
        if (!cancelled) setHasMicrophone(true);
      }
    };

    checkMicrophone();

    // Some browsers support addEventListener; others only support ondevicechange.
    const md: any = navigator?.mediaDevices;
    md?.addEventListener?.("devicechange", checkMicrophone);
    const prevOnDeviceChange = md?.ondevicechange;
    if (md) {
      md.ondevicechange = () => {
        if (typeof prevOnDeviceChange === "function") prevOnDeviceChange();
        checkMicrophone();
      };
    }

    return () => {
      cancelled = true;
      md?.removeEventListener?.("devicechange", checkMicrophone);
      if (md) md.ondevicechange = prevOnDeviceChange ?? null;
    };
  }, []);

  const clearIncoming = () => setIncomingCall(null);
  const clearActive = () => {
    setCall(null);
    setActiveCallLabel(null);
    setMuted(false);
    setServerCallStatus(null);
    // If the device is still registered, we are "ready" again.
    if (deviceRef.current?.state === "registered") {
      setConnectionStatus("ready");
      setIsReady(true);
    } else {
      setConnectionStatus("offline");
      setIsReady(false);
    }
  };

  const wireCallLifecycle = (c: Call) => {
    // Avoid attaching twice
    if ((c as any).__subberWired) return;
    (c as any).__subberWired = true;

    c.on("accept", () => {
      stopRingtone();
      // For OUTGOING calls, Twilio may emit accept when the browser leg is connected to Twilio,
      // not when the callee has answered. We'll drive "connected" via server-side CallStatus polling.
      setConnectionStatus(c.direction === "INCOMING" ? "connected" : "connecting");
      setIsReady(true);
      setCall(c);
      setIncomingCall(null);
      setIncomingCallLabel(null);
      
      // Don't overwrite activeCallLabel if it's already set (e.g. by optimistic lookup)
      // Otherwise it might revert to "Unknown" or phone number.
      setActiveCallLabel(prev => prev || c.parameters.To || "Unknown");
    });

    // When caller hangs up before answer
    c.on("cancel", () => {
      stopRingtone();
      setIncomingCall((prev) => {
        if (prev === c) setIncomingCallLabel(null);
        return prev === c ? null : prev;
      });
      setCall((prev) => {
        if (prev === c) setActiveCallLabel(null);
        return prev === c ? null : prev;
      });
      setMuted(false);
      setConnectionStatus(deviceRef.current?.state === "registered" ? "ready" : "offline");
    });

    c.on("reject", () => {
      stopRingtone();
      setIncomingCall((prev) => {
        if (prev === c) setIncomingCallLabel(null);
        return prev === c ? null : prev;
      });
      setCall((prev) => {
        if (prev === c) setActiveCallLabel(null);
        return prev === c ? null : prev;
      });
      setMuted(false);
      setConnectionStatus(deviceRef.current?.state === "registered" ? "ready" : "offline");
    });

    c.on("disconnect", () => {
      stopRingtone();
      // subtle end tone
      void playTone(440, 90, 0.05);
      setIncomingCall((prev) => {
        if (prev === c) setIncomingCallLabel(null);
        return prev === c ? null : prev;
      });
      setCall((prev) => {
        if (prev === c) setActiveCallLabel(null);
        return prev === c ? null : prev;
      });
      setMuted(false);
      setServerCallStatus(null);
      setConnectionStatus(deviceRef.current?.state === "registered" ? "ready" : "offline");
    });

    c.on("error", (err: any) => {
      console.error("Call error", err);
      stopRingtone();
      // Clear stuck UI on error
      setIncomingCall((prev) => {
        if (prev === c) setIncomingCallLabel(null);
        return prev === c ? null : prev;
      });
      setCall((prev) => {
        if (prev === c) setActiveCallLabel(null);
        return prev === c ? null : prev;
      });
      setMuted(false);
      setServerCallStatus(null);
      setConnectionStatus(deviceRef.current?.state === "registered" ? "ready" : "offline");
    });

    c.on("ringing", (hasEarlyMedia: boolean) => {
      // Only play our outgoing ring if there's no early media (Twilio may already be playing it)
      if (!hasEarlyMedia) void startRingtone("outgoing");
      // For outgoing calls, we want UI to remain "Calling..." while ringing.
      if (c.direction === "OUTGOING") {
        setConnectionStatus("connecting");
      }
    });
  };

  const formatRelativeTime = (isoTimestamp: string) => {
    const date = new Date(isoTimestamp);
    if (isNaN(date.getTime())) return '';
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  };

  const placeOutgoingCall = async (phoneNumber: string, displayName?: string) => {
    if (!device) {
      const reason =
        !hasMicrophone
          ? "No microphone detected"
          : microphonePermission !== "granted"
            ? "Microphone permission required"
            : "System is still starting";
      toast.error(`Microphone not ready: ${reason}`);
      return;
    }

    // Fallback to phone number if no name provided
    setActiveCallLabel(displayName || phoneNumber);

    // If we only have a phone number, try to look up the name immediately
    if (!displayName) {
      GeneralApiService.lookupCustomer(phoneNumber)
        .then(res => {
          if (res.found) {
            setActiveCallLabel(`${res.first_name} ${res.last_name}`);
          }
        })
        .catch(() => {});
    }

    // Ensure mic permission is granted
    const okMic = await requestMicrophonePermission();
    if (!okMic) return;

    // Ensure the device is registered
    if (device.state !== 'registered') {
      try {
        setConnectionStatus('registering');
        await device.register();
      } catch (e: any) {
        console.error("Device register failed", e);
        toast.error("Microphone system not ready (failed to register).");
        return;
      }
    }

    try {
      setConnectionStatus("connecting");
      const outgoing = await device.connect({ params: { To: phoneNumber } });
      callRef.current = outgoing;
      setCall(outgoing);
      wireCallLifecycle(outgoing);
      // Start outgoing ring immediately
      void startRingtone("outgoing");
    } catch (e: any) {
      console.error("Connection failed", e);
      const msg = e?.message || String(e);
      if (msg.toLowerCase().includes("notallowed") || msg.toLowerCase().includes("permission")) {
        toast.error("Microphone permission blocked. Please allow mic access and try again.");
      } else {
        toast.error("Call failed: " + msg);
      }
      if (deviceRef.current?.state === "registered") setConnectionStatus("ready");
      setActiveCallLabel(null);
    }
  };

  // Poll backend call status while we have an active outgoing/incoming call.
  useEffect(() => {
    // IMPORTANT: Do not poll/clear based on backend status while a call is merely *incoming*.
    if (incomingCall && !call) return;
    if (!call) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await GeneralApiService.getMyLatestCall();
        if (cancelled) return;

        if (!res?.active) {
          // If we are locally "connecting" (dialing), the backend might not have the Call record yet
          // (race condition between VoiceWebhook and this poll). Don't kill the UI immediately.
          if (connectionStatusRef.current === "connecting" || connectionStatusRef.current === "registering") {
             return;
          }
          
          // But if we have a live call object that is "open", we should trust it over the backend 
          // (unless backend says explicitly "completed"). 
          // If backend says NO record, but we have a live call object, we might just keep polling?
          // The issue is if the backend doesn't know about the call yet.
          if (call && call.status() === "open") {
             // Local SDK says call is open. Keep it alive.
             return;
          }

          // Server says no active call and we are not in early connecting state: clear UI optimistically.
          stopRingtone();
          clearIncoming();
          clearActive();
          return;
        }

        const status = res.status || null;
        setServerCallStatus(status);

        // Update name if backend found it
        if (res.customer_name) {
          setActiveCallLabel(res.customer_name);
        }

        if (status === "in-progress") {
          setConnectionStatus("connected");
          setIsReady(true);
        } else if (status === "ringing" || status === "queued") {
          setConnectionStatus("connecting");
        }
      } catch {
        // ignore polling errors; SDK events will still manage the UI
      }
    };

    // poll quickly at start, then every ~1.5s
    void poll();
    const t = window.setInterval(() => void poll(), 1500);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [call, incomingCall]);

  useEffect(() => {
    let currentDevice: Device | null = null;
    let refreshInterval: number | null = null;
    let retryTimeout: number | null = null;
    let cancelled = false;

    const scheduleRetry = () => {
      if (cancelled || retryTimeout) return;
      retryTimeout = window.setTimeout(() => {
        retryTimeout = null;
        void initializeDevice();
      }, DEVICE_RETRY_MS);
    };

    const clearRefreshInterval = () => {
      if (refreshInterval) {
        window.clearInterval(refreshInterval);
        refreshInterval = null;
      }
    };

    const refreshDeviceToken = async (targetDevice: Device): Promise<boolean> => {
      try {
        const { token } = await GeneralApiService.getTwilioToken();
        await targetDevice.updateToken(token);
        if (targetDevice.state === "registered" && !callRef.current && !incomingRef.current) {
          setIsReady(true);
          setConnectionStatus("ready");
        }
        return true;
      } catch (error) {
        console.error("Failed to refresh Twilio token", error);
        setIsReady(false);
        setConnectionStatus("error");
        toast.error("Calling system token refresh failed. Please refresh the page if calls do not recover.");
        return false;
      }
    };

    const recoverDevice = async (targetDevice: Device, reason: "token" | "transport") => {
      if (targetDevice.state === "destroyed") return;

      setIsReady(false);
      setConnectionStatus(reason === "token" ? "refreshing-token" : "registering");

      const refreshed = await refreshDeviceToken(targetDevice);
      if (!refreshed || targetDevice.state === "destroyed") {
        scheduleRetry();
        return;
      }

      try {
        if (targetDevice.state !== "registered") {
          await targetDevice.register();
        }
      } catch (error) {
        console.error("Failed to recover Twilio device", error);
        setConnectionStatus("error");
        scheduleRetry();
      }
    };

    const initializeDevice = async () => {
      if (currentDevice && currentDevice.state !== "destroyed") {
        clearRefreshInterval();
        currentDevice.destroy();
        currentDevice = null;
      }

      let newDevice: Device | null = null;
      try {
        const { token } = await GeneralApiService.getTwilioToken();
        
        newDevice = new Device(token, {
            logLevel: 1,
            edge: TWILIO_EDGE,
            tokenRefreshMs: TOKEN_REFRESH_WARNING_MS,
            // Allow incoming calls
            allowIncomingWhileBusy: true,
        });

        // Voice SDK v2 uses registered/unregistered events (not "ready")
        newDevice.on('registering', () => {
          setIsReady(false);
          setConnectionStatus('registering');
        });

        newDevice.on('registered', () => {
          console.log('Twilio.Device Registered!');
          setIsReady(true);
          setConnectionStatus('ready');
        });

        newDevice.on('unregistered', () => {
          setIsReady(false);
          setConnectionStatus('offline');
        });

        newDevice.on('error', (error: any) => {
          console.error('Twilio.Device Error:', error);
          const code = Number(error?.code);
          const message = error?.message || "Unknown calling system error";

          if (tokenErrorCodes.has(code)) {
            void recoverDevice(newDevice, "token");
            toast.error("Calling system token expired or is invalid. Reconnecting...");
            return;
          }

          if (transportErrorCodes.has(code)) {
            void recoverDevice(newDevice, "transport");
            toast.error("Calling connection dropped. Reconnecting...");
            return;
          }

          setConnectionStatus('error');
          toast.error(`Calling system error: ${message}`);
        });

        // Twilio emits this before JWT expiry; refresh proactively.
        newDevice.on("tokenWillExpire", () => {
          void refreshDeviceToken(newDevice);
        });

        newDevice.on('incoming', (conn: Call) => {
          console.log('Incoming call from:', conn.parameters.From);
          setIncomingCall(conn);
          setIncomingCallLabel(conn.parameters.From || "Unknown");
          
          // Try to look up customer name
          if (conn.parameters.From) {
            GeneralApiService.lookupCustomer(conn.parameters.From)
              .then(res => {
                  if (res.found) {
                      setIncomingCallLabel(`${res.first_name} ${res.last_name}`);
                  }
              })
              .catch(() => {});
          }

          wireCallLifecycle(conn);
          void startRingtone("incoming");
        });

        // Register immediately
        setConnectionStatus('registering');
        await newDevice.register();
        setDevice(newDevice);
        currentDevice = newDevice;

        // Fallback refresh in case tokenWillExpire isn't fired in some clients.
        refreshInterval = window.setInterval(() => {
          if (newDevice.state !== "destroyed") {
            void refreshDeviceToken(newDevice);
          }
        }, TOKEN_REFRESH_INTERVAL_MS);

      } catch (error) {
        console.error('Failed to initialize Twilio device', error);
        if (newDevice && newDevice.state !== "destroyed") {
          newDevice.destroy();
        }
        setConnectionStatus('error');
        scheduleRetry();
      }
    };

    initializeDevice();

    return () => {
      cancelled = true;
      clearRefreshInterval();
      if (retryTimeout) {
        window.clearTimeout(retryTimeout);
        retryTimeout = null;
      }
      if (currentDevice) {
        currentDevice.destroy();
      }
    };
  }, []);

  const makeCall = async (phoneNumber: string, displayName?: string) => {
    try {
      const lookup = await GeneralApiService.lookupCustomer(phoneNumber);
      const recentLogs: RecentCallLog[] = lookup?.recent_call_logs || [];
      if (recentLogs.length > 0) {
        setPendingCallConfirmation({
          phoneNumber,
          displayName: displayName || (lookup?.found ? `${lookup.first_name} ${lookup.last_name}`.trim() : displayName),
          recentLogs,
        });
        return;
      }
    } catch {
      // lookup failures shouldn't block calling
    }

    await placeOutgoingCall(phoneNumber, displayName);
  };

  const confirmCallWithWarning = async () => {
    if (!pendingCallConfirmation) return;
    const { phoneNumber, displayName } = pendingCallConfirmation;
    setPendingCallConfirmation(null);
    await placeOutgoingCall(phoneNumber, displayName);
  };

  const cancelCallWithWarning = () => {
    setPendingCallConfirmation(null);
  };

  const formatDuration = (seconds?: number) => {
    const total = Number(seconds || 0);
    if (!total) return "0s";
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    if (!mins) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  const formatCallType = (value?: string) => {
    if (!value) return "Unknown";
    if (value.toLowerCase() === 'inbound') return 'Inbound';
    if (value.toLowerCase() === 'outbound') return 'Outbound';
    return value;
  };

  const getCallerLabel = (log: RecentCallLog) => {
    return log.sales_user || log.sales_username || "Unknown sales user";
  };

  const hangupCall = () => {
    const active = callRef.current || call;
    const incoming = incomingRef.current || incomingCall;

    if (active) {
      stopRingtone();
      try {
        active.disconnect();
      } catch {}
      try {
        (deviceRef.current as any)?.disconnectAll?.();
      } catch {}
      callRef.current = null;
      setCall(null);
      setActiveCallLabel(null);
      setMuted(false);
      setConnectionStatus(deviceRef.current?.state === "registered" ? "ready" : "offline");
      return;
    }

    if (!active && connectionStatus === "connecting") {
      stopRingtone();
      try {
        (deviceRef.current as any)?.disconnectAll?.();
      } catch {}
      setActiveCallLabel(null);
      setMuted(false);
      setConnectionStatus(deviceRef.current?.state === "registered" ? "ready" : "offline");
      return;
    }

    if (incoming) {
      stopRingtone();
      try {
        incoming.reject();
      } catch {}
      incomingRef.current = null;
      setIncomingCall(null);
      setIncomingCallLabel(null);
      setConnectionStatus(deviceRef.current?.state === "registered" ? "ready" : "offline");
    }
  };

  const acceptCall = () => {
      if (incomingCall) {
          const callSid =
            (incomingCall.parameters as any)?.CallSid ||
            (incomingCall.parameters as any)?.callSid ||
            (incomingCall.parameters as any)?.callsid;

          (async () => {
            const okMic = await requestMicrophonePermission();
            if (!okMic) return;

            try {
              if (callSid) {
                await GeneralApiService.claimIncomingCall(String(callSid));
              }
            } catch (e) {
              console.warn("Failed to claim incoming call", e);
            }

            setCall(incomingCall);
            setIncomingCall(null);
            setIncomingCallLabel(null);
            setActiveCallLabel(incomingCall.parameters.From || "Unknown");
            wireCallLifecycle(incomingCall);
            setConnectionStatus("connecting");
            incomingCall.accept();
          })();
      }
  };

  const rejectCall = () => {
      if (incomingCall) {
          stopRingtone();
          incomingCall.reject();
          setIncomingCall(null);
          setIncomingCallLabel(null);
          if (deviceRef.current?.state === "registered") setConnectionStatus("ready");
      }
  };

  const toggleMute = () => {
      if (call) {
          const newMuted = !call.isMuted();
          call.mute(newMuted);
          setMuted(newMuted);
      }
  }

  return (
    <CallContext.Provider value={{ 
        device, 
        call, 
        connectionStatus, 
        isReady, 
        makeCall, 
        requestMicrophonePermission,
        hangupCall,
        acceptCall,
        rejectCall,
        incomingCall,
        activeCallLabel,
        incomingCallLabel,
        hasMicrophone,
        microphonePermission,
        muted,
        toggleMute
    }}>
      {children}
      <Modal open={Boolean(pendingCallConfirmation)} onClose={cancelCallWithWarning}>
        <ModalDialog size="md" sx={{ maxWidth: 640 }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 0.5, pr: 4 }}>
            This customer was called recently
          </Typography>
          <Typography level="body-sm" sx={{ mt: 0, mb: 1.5 }}>
            This customer has call logs in the last 24 hours. Please review before placing another call.
          </Typography>
          <Table size="sm" stripe="odd">
            <thead>
              <tr>
                <th>Type</th>
                <th>Duration</th>
                <th>Sales User</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {(pendingCallConfirmation?.recentLogs || []).slice(0, 3).map((log) => (
                <tr key={log.id}>
                  <td>
                    <Chip size="sm" variant="soft" color={log.call_type === 'inbound' ? 'primary' : 'success'}>
                      {formatCallType(log.call_type)}
                    </Chip>
                  </td>
                  <td>{formatDuration(log.duration)}</td>
                  <td>{getCallerLabel(log)}</td>
                  <td>{formatRelativeTime(log.timestamp)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button variant="plain" color="neutral" onClick={cancelCallWithWarning}>
              Cancel
            </Button>
            <Button color="success" onClick={confirmCallWithWarning}>
              Continue Call
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </CallContext.Provider>
  );
}

export function useCall() {
  const context = useContext(CallContext);
  if (context === undefined) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
}

